import { supportsPushNotifications } from "../../../utils/browserCapabilities";
import { PushNotificationDevicesGetResponseType } from "@sellar-io/api-v2/types";
import { ChangeEvent, ReactElement, ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  DropdownLegacy,
  Icon,
  SpinnerLegacy,
  DropdownLegacyElement,
} from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { ErrorMessage } from "../../../components/errorMessage/ErrorMessage";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { Card, H4, IconType, InputCheckbox } from "@sellar-io/components";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { EnablePushNotificationAlert } from "../../../components/pushNotification/EnablePushNotificationAlert";
import {
  generateAndSetFirebaseMessagingToken,
  getDeviceId,
  usePushNotificationStore,
} from "../../../components/pushNotification/PushNotificationStore";
import { TooltipContainer } from "@sellar-io/components";
import { ACCOUNT } from "../../../constants/routes";
import { useSellarUser } from "../../../hooks";
import { usePushNotificationDeviceCreate } from "../../../hooks/api/v2/pushNotification/usePushNotificationDeviceCreate";
import { usePushNotificationDevicesList } from "../../../hooks/api/v2/pushNotification/usePushNotificationDevicesList";
import { useUser } from "../../../hooks/api/v2/user/useUser";
import { useUserNotificationPreference } from "../../../hooks/api/v2/userNotificationPreference/useUserNotificationPreference";
import { useUserNotificationPreferenceUpdate } from "../../../hooks/api/v2/userNotificationPreference/useUserNotificationPreferenceUpdate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { PushNotificationDeviceDeleteModal } from "./PushNotificationDeviceDeleteModal";
import { PushNotificationDeviceRenameModal } from "./PushNotificationDeviceRenameModal";
import { getUserAccountTypes } from "../../../utils/getUserAccountTypes";

type UserNotificationPreferenceState = {
  emailConnectionRequested: boolean;
  emailConnectionAccepted: boolean;
  emailMessages: boolean;
  emailOrderRequested: boolean;
  emailOrderAccepted: boolean;
  emailOrderDispatched: boolean;
  emailOrderDelivered: boolean;
  emailOrderCancelled: boolean;
  emailOrderCommentCreated: boolean;
  emailOrderPaid: boolean;
  emailOrderPaymentExpiry: boolean;
  pushConnectionRequested: boolean;
  pushConnectionAccepted: boolean;
  pushMessages: boolean;
  pushOrderRequested: boolean;
  pushOrderAccepted: boolean;
  pushOrderDispatched: boolean;
  pushOrderDelivered: boolean;
  pushOrderCancelled: boolean;
  pushOrderCommentCreated: boolean;
  pushOrderPaid: boolean;
  pushOrderPaymentExpiry: boolean;
};

export const NotificationPreferencesLegacy = (): ReactElement | null => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();

  if (!user) throw new Error("Cannot find user");

  const navigate = useNavigate();

  const [message, setMessage] = useState<string>();

  const userQuery = useUser(parseInt(user.id.toString(), 10));

  const notificationPreferenceQuery = useUserNotificationPreference(
    user.UserNotificationPreferenceId,
  );

  const notificationPreferenceMutation = useUserNotificationPreferenceUpdate(
    user.UserNotificationPreferenceId,
  );

  const pushNotificationDeviceCreateMutation =
    usePushNotificationDeviceCreate();

  const pushNotificationDevicesQuery = usePushNotificationDevicesList({});
  const deviceId = getDeviceId();
  const existingDevice = pushNotificationDevicesQuery.data?.pages.flatMap(
    (page) => page.filter((device) => device.deviceId === deviceId),
  );

  const deviceDisabled = !existingDevice || existingDevice.length == 0;

  const notificationNotGranted = () => {
    if (!supportsPushNotifications()) return true;
    return Notification.permission !== "granted";
  };

  const pushNotificationsDisabled = notificationNotGranted() || deviceDisabled;

  const {
    retailer: hasBuyerAccount,
    supplier: hasSupplierAccount,
    dual: hasDualAccount,
  } = getUserAccountTypes(user);

  const triggerPushPrompt = async () => {
    const notificationToken = await generateAndSetFirebaseMessagingToken();
    if (notificationToken) {
      pushNotificationDeviceCreateMutation.mutate({
        notificationToken,
        deviceId,
      });
    } else {
      usePushNotificationStore.getState().setGuideActive(true);
      return;
    }
  };

  const handleChange = async (
    preference: keyof UserNotificationPreferenceState,
    value?: boolean,
  ) => {
    const preferenceNewState =
      value ?? !notificationPreferenceQuery.data?.[preference];
    notificationPreferenceMutation.mutate(
      {
        [preference]: preferenceNewState,
      },
      {
        onError: () =>
          setMessage("Failed to update preference. Please try again"),
      },
    );
  };

  const handlePushChange = async (
    preference: keyof UserNotificationPreferenceState,
  ) => {
    const pushBeingEnabled = !notificationPreferenceQuery.data?.[preference];
    if (pushBeingEnabled) {
      await triggerPushPrompt();
    }
    handleChange(preference);
  };

  return (
    <>
      <NavigationBar
        title={"Notifications"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(ACCOUNT)}
      />
      {notificationPreferenceQuery.isLoading ||
      userQuery.isLoading ||
      pushNotificationDevicesQuery.isLoading ? (
        <SpinnerLegacy />
      ) : notificationPreferenceQuery.isError ? (
        <ErrorMessage />
      ) : (
        notificationPreferenceQuery.isSuccess &&
        userQuery.isSuccess &&
        pushNotificationDevicesQuery.isSuccess && (
          <PageContainer>
            <Card className={twMerge("bg-white", "mx-auto px-4 py-5")}>
              <H4>Notifications</H4>
              <div>
                Manage your notifications. Push notifications from Sellar are
                displayed by your approved devices.
              </div>
              {user.email !== business?.email && (
                <div className={"pt-4"}>
                  Get email notifications from Sellar and your customers sent to
                  your login email address: <strong>{user.email}</strong>.
                </div>
              )}
            </Card>

            {message && <Alert type={"information"} message={message} />}

            <Card
              className={twMerge(
                "bg-white",
                "mx-auto mt-2 rounded-b-none px-4 py-5",
              )}
            >
              <H4>Devices</H4>
              <div>
                Listed below are the devices on which your account has approved
                push notifications from Sellar.
              </div>
            </Card>

            <Card
              className={twMerge(
                "bg-white",
                "mx-auto mt-[2px] rounded-t-none px-4 py-5",
              )}
            >
              <EnablePushNotificationAlert
                title="Enable push notifications on this device?"
                message="You need to turn on notifications for this current device to edit your push notification preferences."
                className="mb-4 w-full"
                deviceDisabled={deviceDisabled}
              />
              <Card className={twMerge("bg-white", "border border-zinc-300")}>
                <div className="rounded-t-lg bg-zinc-50 px-4 py-2 font-bold">
                  Device name
                </div>
                {pushNotificationDevicesQuery.data.pages.map((page) =>
                  page.map((device) => (
                    <Device key={device.id} device={device} />
                  )),
                )}
              </Card>
            </Card>

            <Card
              className={twMerge(
                "bg-white",
                "mx-auto mt-2 rounded-b-none px-4 py-5",
              )}
            >
              <H4>Preferences</H4>
              <div>Choose which notifications you'd like to receive.</div>
            </Card>

            <Card
              className={twMerge(
                "bg-white",
                "mx-auto mt-[2px] rounded-t-none px-4 py-5",
              )}
            >
              <div className="mb-4">
                <div className={"pb-4"}>
                  <Row
                    text="Connections"
                    textClass="font-bold"
                    rowClass="border-t bg-zinc-50"
                  >
                    <Title text="Email" />
                    <Title text="Push" />
                  </Row>

                  <Row text="Connection request received">
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data
                          .emailConnectionRequested
                      }
                      onChange={() => handleChange("emailConnectionRequested")}
                    />

                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.pushConnectionRequested
                      }
                      onChange={() =>
                        handlePushChange("pushConnectionRequested")
                      }
                      disabled={pushNotificationsDisabled}
                      tooltip="Enable push notifications on this device to edit preference"
                    />
                  </Row>

                  {hasBuyerAccount && (
                    <Row
                      text={`Connection request accepted${hasDualAccount ? "*" : ""}`}
                    >
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data
                            .emailConnectionAccepted
                        }
                        onChange={() => handleChange("emailConnectionAccepted")}
                      />
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data
                            .pushConnectionAccepted
                        }
                        onChange={() =>
                          handlePushChange("pushConnectionAccepted")
                        }
                        disabled={pushNotificationsDisabled}
                        tooltip="Enable push notifications on this device to edit preference"
                      />
                    </Row>
                  )}
                </div>

                <div className={"py-4"}>
                  <Row
                    text="Messages"
                    textClass="font-bold"
                    rowClass="border-t bg-zinc-50"
                  >
                    <Title text="Email" />
                    <Title text="Push" />
                  </Row>

                  <Row text="Direct message received">
                    <PreferenceCheckbox
                      checked={notificationPreferenceQuery.data.emailMessages}
                      onChange={() => handleChange("emailMessages")}
                    />
                    <PreferenceCheckbox
                      checked={notificationPreferenceQuery.data.pushMessages}
                      onChange={() => handlePushChange("pushMessages")}
                      disabled={pushNotificationsDisabled}
                      tooltip="Enable push notifications on this device to edit preference"
                    />
                  </Row>
                </div>

                <div className={"py-4"}>
                  <Row
                    text="Order Events"
                    textClass="font-bold"
                    rowClass="border-t bg-zinc-50"
                  >
                    <Title text="Email" />
                    <Title text="Push" />
                  </Row>

                  <Row text="Order requested">
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.emailOrderRequested
                      }
                      onChange={() => handleChange("emailOrderRequested")}
                    />
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.pushOrderRequested
                      }
                      onChange={() => handlePushChange("pushOrderRequested")}
                      disabled={pushNotificationsDisabled}
                      tooltip="Enable push notifications on this device to edit preference"
                    />
                  </Row>

                  {hasBuyerAccount && (
                    <Row text={`Order accepted${hasDualAccount ? "*" : ""}`}>
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data.emailOrderAccepted
                        }
                        onChange={() => handleChange("emailOrderAccepted")}
                      />
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data.pushOrderAccepted
                        }
                        onChange={() => handlePushChange("pushOrderAccepted")}
                        disabled={pushNotificationsDisabled}
                        tooltip="Enable push notifications on this device to edit preference"
                      />
                    </Row>
                  )}

                  {hasBuyerAccount && (
                    <Row text={`Order dispatched${hasDualAccount ? "*" : ""}`}>
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data.emailOrderDispatched
                        }
                        onChange={() => handleChange("emailOrderDispatched")}
                      />
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data.pushOrderDispatched
                        }
                        onChange={() => handlePushChange("pushOrderDispatched")}
                        disabled={pushNotificationsDisabled}
                        tooltip="Enable push notifications on this device to edit preference"
                      />
                    </Row>
                  )}

                  {hasBuyerAccount && (
                    <Row text={`Order delivered${hasDualAccount ? "*" : ""}`}>
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data.emailOrderDelivered
                        }
                        onChange={() => handleChange("emailOrderDelivered")}
                      />
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data.pushOrderDelivered
                        }
                        onChange={() => handlePushChange("pushOrderDelivered")}
                        disabled={pushNotificationsDisabled}
                        tooltip="Enable push notifications on this device to edit preference"
                      />
                    </Row>
                  )}

                  <Row text="Order cancelled">
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.emailOrderCancelled
                      }
                      onChange={() => handleChange("emailOrderCancelled")}
                    />
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.pushOrderCancelled
                      }
                      onChange={() => handlePushChange("pushOrderCancelled")}
                      disabled={pushNotificationsDisabled}
                      tooltip="Enable push notifications on this device to edit preference"
                    />
                  </Row>

                  <Row text="Comment added to order">
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data
                          .emailOrderCommentCreated
                      }
                      onChange={() => handleChange("emailOrderCommentCreated")}
                    />
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.pushOrderCommentCreated
                      }
                      onChange={() =>
                        handlePushChange("pushOrderCommentCreated")
                      }
                      disabled={pushNotificationsDisabled}
                      tooltip="Enable push notifications on this device to edit preference"
                    />
                  </Row>

                  <Row text="Order payment expiry (Sellar Credit only)">
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.emailOrderPaymentExpiry
                      }
                      onChange={() => handleChange("emailOrderPaymentExpiry")}
                    />
                    <PreferenceCheckbox
                      checked={
                        notificationPreferenceQuery.data.pushOrderPaymentExpiry
                      }
                      onChange={() =>
                        handlePushChange("pushOrderPaymentExpiry")
                      }
                      disabled={pushNotificationsDisabled}
                      tooltip="Enable push notifications on this device to edit preference"
                    />
                  </Row>

                  {hasSupplierAccount && (
                    <Row
                      text={`Order paid for by card payment link${hasDualAccount ? "**" : ""}`}
                    >
                      <PreferenceCheckbox
                        checked={
                          notificationPreferenceQuery.data.emailOrderPaid
                        }
                        onChange={() => handleChange("emailOrderPaid")}
                      />
                      <PreferenceCheckbox
                        checked={notificationPreferenceQuery.data.pushOrderPaid}
                        onChange={() => handlePushChange("pushOrderPaid")}
                        disabled={pushNotificationsDisabled}
                        tooltip="Enable push notifications on this device to edit preference"
                      />
                    </Row>
                  )}
                </div>
                {hasDualAccount && (
                  <div className="font-bold">
                    <p>*Only applicable to your buyer account(s)</p>
                    <p>**Only applicable to your supplier account(s)</p>
                  </div>
                )}
              </div>
            </Card>
          </PageContainer>
        )
      )}
    </>
  );
};

const Device = ({
  device,
}: {
  device: PushNotificationDevicesGetResponseType[number];
}) => {
  const getDeviceIcon = (deviceType: string): IconType => {
    return deviceType.includes("mobile") ? "CELLPHONE" : "DESKTOP";
  };

  const [deviceRenameModal, setDeviceRenameModal] = useState(false);
  const [deviceDeleteModal, setDeviceDeleteModal] = useState(false);

  return (
    <>
      <div
        key={device.id}
        className="flex items-center border-t border-zinc-300 px-5 py-3"
      >
        <Icon icon={getDeviceIcon(device.deviceId)} className="mr-3" />
        {device.deviceName}
        <DropdownLegacy className={"ml-auto"}>
          <DropdownLegacyElement onClick={() => setDeviceRenameModal(true)}>
            Rename device
          </DropdownLegacyElement>
          <DropdownLegacyElement
            onClick={() => setDeviceDeleteModal(true)}
            className={"text-red-400"}
          >
            Remove device permissions
          </DropdownLegacyElement>
        </DropdownLegacy>
      </div>
      <PushNotificationDeviceRenameModal
        device={device}
        onDismiss={() => setDeviceRenameModal(false)}
        active={deviceRenameModal}
      />
      <PushNotificationDeviceDeleteModal
        device={device}
        onDismiss={() => setDeviceDeleteModal(false)}
        active={deviceDeleteModal}
      />
    </>
  );
};

export const Row = ({
  text,
  textClass,
  rowClass,
  children,
}: {
  text: string;
  textClass?: string;
  rowClass?: string;
  children: ReactNode;
}) => {
  return (
    <div
      className={twMerge(
        "@container flex items-center border-b border-zinc-300 py-2 pl-4",
        rowClass,
      )}
    >
      <span
        className={twMerge(
          "mr-auto block max-w-32 @lg:max-w-64 @2xl:max-w-full",
          textClass,
        )}
      >
        {text}
      </span>
      {children}
    </div>
  );
};

export const Title = ({ text }: { text: string }) => {
  return (
    <span className="w-16 px-2 py-[6px] font-bold @sm:w-20 @xl:w-24">
      {text}
    </span>
  );
};

export const PreferenceCheckbox = ({
  checked,
  onChange,
  label,
  disabled,
  tooltip,
}: {
  checked: boolean;
  onChange?: (x?: ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  disabled?: boolean;
  tooltip?: string;
}) => {
  return (
    <TooltipContainer
      enable={!!disabled}
      placement="top"
      text={tooltip ?? "Text notifications are being discontinued"}
    >
      <div>
        <InputContainer className="w-16 border-none bg-transparent @sm:w-20 @xl:w-24">
          <InputCheckbox
            required={false}
            checked={checked}
            onChange={(e) => (onChange ? onChange(e) : null)}
            disabled={disabled}
          />
          {!!label && <InputLabel>{label}</InputLabel>}
        </InputContainer>
      </div>
    </TooltipContainer>
  );
};
